import React, { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Auth } from 'aws-amplify';

const AuthContext = createContext(null);

AuthContext.displayName = 'AuthContext';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children, amplifyUser }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadCurrentUser = async () => {
      if (!amplifyUser) {
        setCurrentUser(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const cognitoUser = await Auth.currentAuthenticatedUser();
        const attributes = cognitoUser.attributes;

        setCurrentUser({
          username: amplifyUser.username,
          email: attributes.email,
          role: attributes['custom:userRole'] || 'Admin',
          tenantId: attributes['custom:tenantId'],
        });
      } catch (err) {
        console.error('Error loading user data:', err);
        setError(err.message || 'Failed to load user data');
      } finally {
        setLoading(false);
      }
    };

    loadCurrentUser();
  }, [amplifyUser]);

  const hasRole = (role) => {
    return currentUser?.role === role;
  };

  const isAdmin = () => hasRole('Admin');

  const value = {
    currentUser,
    loading,
    error,
    hasRole,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
  amplifyUser: PropTypes.object,
};
